import type {
  ButtonProps,
  ButtonProps as RACButtonProps,
} from "react-aria-components";

import { XIcon } from "lucide-react";
import React from "react";
import {
  DialogTrigger,
  OverlayTriggerStateContext,
} from "react-aria-components";

import { Button } from "@/components/button";
import { Dialog, DialogBody, DialogFooter } from "@/components/dialog";
import { Modal } from "@/components/modal";
import { useWhitelabel } from "@/hooks/useWhitelabel";

interface UpgradeModalProps {
  bodyText: string;
  children: React.ReactNode;
  headerText: string;
  heroImage: string;
  onClose?: RACButtonProps["onPress"];
  onConfirm?: RACButtonProps["onPress"];
}

export function UpgradeModal({
  bodyText,
  children,
  headerText,
  heroImage,
  onConfirm,
}: UpgradeModalProps) {
  const { isWhitelabel } = useWhitelabel();

  return (
    <DialogTrigger>
      {children}
      <Modal isDismissable>
        <Dialog>
          {({ close }) => (
            <>
              <DialogBody className="gap-none">
                <div className="w-full flex justify-end mb-md">
                  <DialogCloseButton />
                </div>
                <img alt="" src={heroImage} />
                <div className="relative flex gap-xl text-center mt-3xl">
                  <div className="mx-auto flex w-[464px] flex-col">
                    <span className="text-lg font-semibold text-text-primary">
                      {headerText}
                    </span>
                    <span className="text-sm text-text-tertiary">
                      {bodyText}
                    </span>
                  </div>
                </div>
              </DialogBody>

              <DialogFooter className="pt-4xl">
                {!isWhitelabel && (
                  <div className="flex w-full gap-lg">
                    <Button
                      className="w-full"
                      color="gray"
                      onPress={close}
                      variant="secondary"
                    >
                      Not Today
                    </Button>
                    <Button className="w-full" onPress={onConfirm}>
                      Upgrade Plan
                    </Button>
                  </div>
                )}
              </DialogFooter>
            </>
          )}
        </Dialog>
      </Modal>
    </DialogTrigger>
  );
}

export function DialogCloseButton(props: ButtonProps) {
  const state = React.useContext(OverlayTriggerStateContext)!;

  const { "aria-label": ariaLabel, onPress, ...restProps } = props;

  return (
    <Button
      {...restProps}
      className="text-text-primary"
      onPress={(e) => {
        state.close();
        onPress?.(e);
      }}
      variant="unstyled"
    >
      <XIcon aria-label={ariaLabel ?? "Close"} size={24} />
    </Button>
  );
}
